import { forwardRef } from 'react';

interface SectionProps {
  id: string;
  className?: string;
  children: React.ReactNode;
}

const Section = forwardRef<HTMLElement, SectionProps>(({ id, className = '', children }, ref) => (
  <section id={id} ref={ref} className={`py-14 md:py-20 px-4 md:px-6 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
));

Section.displayName = 'Section';

/* ───── #media: 媒体视频卡片 5项 ───── */
export function MediaSection() {
  const videos = [
    { title: '《烈火追击》终极预告', type: '预告片', duration: '02:35', views: '128万' },
    { title: '《暗夜追踪》精彩片段', type: '花絮', duration: '04:12', views: '96万' },
    { title: '《孤胆英雄》幕后特辑', type: '幕后', duration: '08:47', views: '73万' },
    { title: '暑期档必看推荐合集', type: '盘点', duration: '12:20', views: '205万' },
    { title: '悬疑片名场面盘点', type: '混剪', duration: '06:55', views: '157万' },
  ];

  return (
    <Section id="media" className="bg-background-50">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        精彩预告
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        热门影片精彩片段与预告合集
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {videos.map((v, i) => (
          <div
            key={i}
            className={`group bg-background-100 rounded-2xl border border-background-300/30 overflow-hidden hover:border-primary-500/20 transition-colors cursor-pointer ${i === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
          >
            <div className={`relative ${i === 0 ? 'aspect-video' : 'aspect-[16/10]'} overflow-hidden`}>
              <div className="absolute inset-0 bg-background-300/40 flex items-center justify-center">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary-500/80 flex items-center justify-center group-hover:bg-primary-400 transition-colors">
                  <i className="ri-play-fill text-2xl md:text-3xl text-background-50 ml-1"></i>
                </div>
              </div>
              <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-background-950/70 backdrop-blur-sm text-xs text-foreground-200 whitespace-nowrap">
                {v.duration}
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-full bg-primary-500/10 text-xs text-primary-400 border border-primary-500/20 whitespace-nowrap">
                  {v.type}
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  <i className="ri-eye-line mr-1"></i>{v.views}
                </span>
              </div>
              <h3 className="text-sm font-medium text-foreground-200">{v.title}</h3>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───── #topics: 话题标签 9项 ───── */
export function TopicsSection() {
  const topics = [
    { name: '#暑期档必看', heat: '沸', count: '2.8万讨论' },
    { name: '#国产动作大片', heat: '热', count: '1.6万讨论' },
    { name: '#今日更新片单', heat: '新', count: '1.2万讨论' },
    { name: '#悬疑片推荐', heat: '热', count: '9,800讨论' },
    { name: '#科幻迷聚集地', heat: '热', count: '7,500讨论' },
    { name: '#经典港片重温', heat: '', count: '6,300讨论' },
    { name: '#高分华语片', heat: '', count: '5,900讨论' },
    { name: '#周末观影指南', heat: '', count: '4,200讨论' },
    { name: '#导演作品合集', heat: '', count: '3,800讨论' },
  ];

  const getHeatClass = (heat: string) => {
    if (heat === '沸') return 'bg-primary-500 text-background-50';
    if (heat === '热') return 'bg-accent-500/80 text-background-50';
    if (heat === '新') return 'bg-secondary-500/80 text-background-50';
    return 'bg-background-300/40 text-foreground-500';
  };

  return (
    <Section id="topics" className="bg-background-100">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        热门话题
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        影迷们正在热烈讨论的话题
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {topics.map((t) => (
          <a
            key={t.name}
            href="#topics"
            className="flex items-center justify-between bg-background-50 rounded-xl border border-background-300/30 p-4 hover:border-primary-500/20 transition-colors cursor-pointer"
            onClick={(e) => e.preventDefault()}
          >
            <div className="flex items-center gap-3 min-w-0">
              {t.heat && (
                <span className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${getHeatClass(t.heat)}`}>
                  {t.heat}
                </span>
              )}
              <span className="text-sm font-medium text-foreground-200 truncate">{t.name}</span>
            </div>
            <span className="text-xs text-foreground-500 flex-shrink-0 ml-3 whitespace-nowrap">{t.count}</span>
          </a>
        ))}
      </div>
    </Section>
  );
}

/* ───── #faq: 常见问答 6项 ───── */
export function FaqSection() {
  const faqs = [
    {
      q: '91电影是否需要注册或付费？',
      a: '完全不需要。91电影在线观看平台对所有用户免费开放，无需注册账号、无需充值会员，打开浏览器即可直接观看全部影片。我们通过站点运营维持服务，致力于让每个人都能享受免费高清在线影院的观影体验。',
    },
    {
      q: '91电影在线播放的画质如何？',
      a: '我们提供从720P到1080P全高清的多种清晰度选择，大部分热门影片支持1080P超清画质。采用先进的视频编码技术，在保证画质的同时优化带宽占用，让您在不同网络环境下都能获得最佳观影效果。',
    },
    {
      q: '为什么91电影播放如此流畅不卡顿？',
      a: '91电影部署了覆盖全国主要城市的分布式CDN加速网络，智能选路系统会根据您的网络环境自动匹配最优节点。同时我们采用了自适应码率技术，实时监测网络状况动态调整，确保流畅播放体验。',
    },
    {
      q: '91电影的影片多久更新一次？',
      a: '我们保持每日更新，平均每天新增36部以上影片资源。国产热门大片通常在院线下映后尽快上线，同时我们会定期推出主题策划专题，如「暑期档合集」「悬疑片周」等，让您不错过任何好片。',
    },
    {
      q: '手机或平板上能用91电影在线观看吗？',
      a: '可以。91电影在线播放平台全面适配移动端设备，在iPhone、Android手机及iPad等平板上均可流畅使用。界面针对触控操作优化，支持横竖屏自由切换，移动网络下也做了流量优化。',
    },
    {
      q: '观看91电影安全吗？会有广告或病毒吗？',
      a: '绝对安全。91电影免费在线平台坚持纯净观影体验，不包含任何弹窗广告、恶意跳转或捆绑软件。所有影片资源在上线前都经过安全检测，您可以放心观看。我们建议通过浏览器直接访问本站，以获得最佳安全保障。',
    },
  ];

  return (
    <Section id="faq" className="bg-background-50">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100 text-center mb-3">
        常见问题
      </h2>
      <p className="text-sm text-foreground-500 text-center max-w-lg mx-auto mb-10">
        关于91电影的常见疑问解答
      </p>
      <div className="max-w-3xl mx-auto space-y-3">
        {faqs.map((faq, i) => (
          <details key={i} className="group bg-background-100 rounded-xl border border-background-300/30 overflow-hidden" open={i === 0}>
            <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
              <h3 className="text-sm md:text-base font-medium text-foreground-200 pr-4">{faq.q}</h3>
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-background-300/30 flex items-center justify-center group-open:bg-primary-500/20 transition-colors">
                <i className="ri-arrow-down-s-line text-sm text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </div>
            </summary>
            <div className="px-5 pb-5">
              <p className="text-sm text-foreground-400 leading-relaxed">{faq.a}</p>
            </div>
          </details>
        ))}
      </div>
    </Section>
  );
}